/**
 * Embed Live Edit
 *
 * Document live editing in iframe mode using Collabora Online.
 *
 * `mode=view` opens a read-only session (WOPI UserCanWrite=false) — what a
 * framing module asks for once its own record of the document is final. An
 * edit session follows the file (LiveEditor): when it is locked, sent for
 * signing or agreement, filed, or the person's access drops, the editor
 * reopens read only with a banner saying why.
 *
 * Tells the framer (docs/INTEGRATION_GUIDE.md), at its origin:
 *   {type: 'DOCS_EDITOR_STATE', fileId, mode: 'edit'|'view', reason} — once
 *     known and on every change; reason null | locked | signed | agreement |
 *     record | no_edit_access
 *   {type: 'DOCS_EDITOR_ERROR', fileId, code: 'no_access'|'not_found'|'error', status}
 *     — when the file read or the editor token is refused
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmbedAuth } from '@/shared/contexts/EmbedAuthContext';
import { LiveEditor } from '@/modules/collabora-editor/LiveEditor.jsx';
import { errorMessage, stateMessage } from '@/modules/collabora-editor/liveState.js';
import { postToParent } from '@/shared/utils/embedParentOrigin';
import { useSearchParams } from 'react-router-dom';

export default function EmbedLiveEdit() {
    const { t } = useTranslation(['common']);
    useEmbedAuth();
    const [searchParams] = useSearchParams();
    const fileId = searchParams.get('fileId');
    const requested = searchParams.get('mode') === 'view' ? 'view' : 'edit';

    const tellState = useCallback(
        (state) => postToParent(stateMessage(fileId, state), { hold: true }),
        [fileId],
    );
    const tellError = useCallback(
        (failure) => postToParent(errorMessage(fileId, failure), { hold: true }),
        [fileId],
    );

    if (!fileId) {
        return (
            <div className="flex items-center justify-center h-dvh text-gray-400">
                {t('common:embed.noFileId')}
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100dvh' }}>
            <LiveEditor
                key={`${fileId}:${requested}`}
                fileId={fileId}
                requested={requested}
                onStateChange={tellState}
                onError={tellError}
            />
        </div>
    );
}
